import { FormFieldMessage } from './FormFieldMessage';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CallType, CallFormData } from '@/hooks/useCallCreationFlow';
import { Calendar, DollarSign, FileText, Users, CheckCircle2, X, Check, Search, Plus } from 'lucide-react';
import { useState } from 'react';
import type { UserChannel } from '@/hooks/useUserChannels';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useDomains } from '@/hooks/useDomainHierarchy';

// Channel Giblet icons
import ispaiGiblet from '@/assets/giblets/ispai-giblet.png';
import curtGiblet from '@/assets/giblets/curt-giblet.png';
import oilGasGiblet from '@/assets/giblets/oil-gas-giblet.png';
import usaGiblet from '@/assets/giblets/usa-giblet.png';
import defaultGiblet from '@/assets/giblets/default-giblet.png';

// Channel mapping with Giblet icons
const CHANNEL_GIBLET_MAP: Record<string, { name: string; icon: string }> = {
  '28570003-dd67-4842-bc21-784df8caaf94': { name: 'iSPAI', icon: ispaiGiblet },
  'ec9b3b57-263a-4713-b3ed-5fe67808486e': { name: 'CURT', icon: curtGiblet },
  '69d024ae-f179-40d9-9ac5-88aaf46ce866': { name: 'Oil & Gas', icon: oilGasGiblet },
  '536ba1bb-4a0a-4024-a19c-f77b566bd860': { name: 'USA/GSA', icon: usaGiblet },
};

// Channel Source Selection Message (with Giblet icons)
interface ChannelSourceSelectionMessageProps {
  userChannels: UserChannel[];
  userAvatarUrl?: string | null;
  userName?: string;
  onSelect: (sourceType: 'channel' | 'personal', channelId?: string) => void;
  disabled?: boolean;
  contextLabel?: string; // e.g., "Call" or "Knowledge Base"
}

export const ChannelSourceSelectionMessage = ({ 
  userChannels, 
  userAvatarUrl, 
  userName,
  onSelect, 
  disabled,
  contextLabel = 'Call'
}: ChannelSourceSelectionMessageProps) => {
  const [selected, setSelected] = useState<string>('');

  const handleSelect = () => {
    if (selected === 'personal') {
      onSelect('personal');
    } else if (selected) {
      onSelect('channel', selected);
    }
  };

  // Get unique channels with Giblet icons
  const channelsWithIcons = userChannels.map(channel => ({
    ...channel,
    gibletIcon: CHANNEL_GIBLET_MAP[channel.channel_id]?.icon || defaultGiblet,
    displayName: CHANNEL_GIBLET_MAP[channel.channel_id]?.name || channel.channels.name,
  }));

  const questionText = contextLabel === 'Knowledge Base' 
    ? 'Where should this Knowledge Base be created?'
    : 'Where is this Call coming from?';
  
  const subtitleText = contextLabel === 'Knowledge Base'
    ? 'Select a channel or create from your personal profile'
    : 'Select a channel or send from your personal profile';

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">{questionText}</p>
        <p className="text-sm text-muted-foreground">{subtitleText}</p>
        
        <div className="grid grid-cols-2 gap-3">
          {/* Channel options */}
          {channelsWithIcons.map((channel) => (
            <button
              key={channel.channel_id}
              type="button"
              onClick={() => setSelected(channel.channel_id)}
              disabled={disabled}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
                "hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-primary/50",
                selected === channel.channel_id 
                  ? "border-primary bg-primary/10" 
                  : "border-border/50 bg-background"
              )}
            >
              <img 
                src={channel.gibletIcon} 
                alt={channel.displayName}
                className="h-12 w-12 rounded-full object-cover"
              />
              <span className="text-sm font-medium text-center">{channel.displayName}</span>
            </button>
          ))}
          
          {/* Personal (GibNifty) option */}
          <button
            type="button"
            onClick={() => setSelected('personal')}
            disabled={disabled}
            className={cn(
              "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
              "hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-primary/50",
              selected === 'personal' 
                ? "border-primary bg-primary/10" 
                : "border-border/50 bg-background"
            )}
          >
            <Avatar className="h-12 w-12">
              <AvatarImage src={userAvatarUrl || undefined} alt={userName || 'You'} />
              <AvatarFallback className="bg-primary/20">
                {userName?.charAt(0).toUpperCase() || 'Y'}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-center">Personal</span>
          </button>
        </div>
        
        <Button 
          onClick={handleSelect} 
          disabled={!selected || disabled} 
          className="w-full"
        >
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// Legacy Channel Selection Message (kept for backward compatibility)
interface ChannelSelectionMessageProps {
  channels: UserChannel[];
  onSelect: (channelId: string) => void;
  disabled?: boolean;
}

export const ChannelSelectionMessage = ({ channels, onSelect, disabled }: ChannelSelectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Select Channel</p>
        <p className="text-sm text-muted-foreground">Choose the channel for this Call</p>
        <RadioGroup value={selected} onValueChange={setSelected} disabled={disabled}>
          {channels.map((channel) => (
            <div key={channel.channel_id} className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
              <RadioGroupItem value={channel.channel_id} id={channel.channel_id} />
              <Label htmlFor={channel.channel_id} className="cursor-pointer flex-1">
                {channel.channels.name} - {channel.organization_name}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <Button onClick={() => selected && onSelect(selected)} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// Domain Selection Message
interface DomainSelectionMessageProps {
  domains: Array<{ id: string; name: string; description?: string }>;
  onSelect: (domainId: string) => void;
  disabled?: boolean;
}

export const DomainSelectionMessage = ({ domains, onSelect, disabled }: DomainSelectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Select Domain</p>
        <p className="text-sm text-muted-foreground">Choose the functional domain for this Call</p>
        <Select value={selected} onValueChange={setSelected} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select a domain..." />
          </SelectTrigger>
          <SelectContent>
            {domains.map((domain) => (
              <SelectItem key={domain.id} value={domain.id}>
                {domain.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => selected && onSelect(selected)} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// Subdomain Selection Message
interface SubdomainSelectionMessageProps {
  subdomains: Array<{ id: string; name: string; description?: string }>;
  onSelect: (subdomainId: string) => void;
  disabled?: boolean;
}

export const SubdomainSelectionMessage = ({ subdomains, onSelect, disabled }: SubdomainSelectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Select Subdomain (Optional)</p>
        <p className="text-sm text-muted-foreground">Narrow down the specific area if applicable</p>
        <Select value={selected} onValueChange={setSelected} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select a subdomain..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="skip">Skip - No specific subdomain</SelectItem>
            {subdomains.map((subdomain) => (
              <SelectItem key={subdomain.id} value={subdomain.id}>
                {subdomain.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => selected && onSelect(selected)} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// CfK Connection Message
interface CfKConnectionMessageProps {
  onSelect: (connected: boolean) => void;
  disabled?: boolean;
}

export const CfKConnectionMessage = ({ onSelect, disabled }: CfKConnectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Connected to Call for Solution?</p>
        <p className="text-sm text-muted-foreground">Is this knowledge supporting a specific CfS?</p>
        <RadioGroup value={selected} onValueChange={setSelected} disabled={disabled}>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="yes" id="cfk-yes" />
            <Label htmlFor="cfk-yes" className="cursor-pointer">Yes, connected to a CfS</Label>
          </div>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="no" id="cfk-no" />
            <Label htmlFor="cfk-no" className="cursor-pointer">No, standalone CfK</Label>
          </div>
        </RadioGroup>
        <Button onClick={() => selected && onSelect(selected === 'yes')} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// CfK Bounty Connection Message
interface CfKBountyConnectionMessageProps {
  onSelect: (hasBounty: boolean) => void;
  disabled?: boolean;
}

export const CfKBountyConnectionMessage = ({ onSelect, disabled }: CfKBountyConnectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Does the CfS have a Solution Bounty?</p>
        <p className="text-sm text-muted-foreground">This affects attribution and rewards for contributors</p>
        <RadioGroup value={selected} onValueChange={setSelected} disabled={disabled}>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="yes" id="bounty-yes" />
            <Label htmlFor="bounty-yes" className="cursor-pointer">Yes, CfS has a bounty</Label>
          </div>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="no" id="bounty-no" />
            <Label htmlFor="bounty-no" className="cursor-pointer">No bounty on CfS</Label>
          </div>
        </RadioGroup>
        <Button onClick={() => selected && onSelect(selected === 'yes')} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// No hardcoded fallback - domains are loaded from the database based on channel

interface CallTypeMessageProps {
  onSelect: (type: CallType) => void;
  disabled?: boolean;
}

export const CallTypeMessage = ({ onSelect, disabled }: CallTypeMessageProps) => {
  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">What type of Call would you like to create?</p>
        <RadioGroup onValueChange={(value) => onSelect(value as CallType)} disabled={disabled}>
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="solution" id="solution" className="mt-1" />
            <Label htmlFor="solution" className="cursor-pointer flex-1">
              <div className="font-medium">Call for Solution</div>
              <div className="text-sm text-muted-foreground">Request innovative solutions to challenges</div>
            </Label>
          </div>
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="knowledge" id="knowledge" className="mt-1" />
            <Label htmlFor="knowledge" className="cursor-pointer flex-1">
              <div className="font-medium">Call for Knowledge</div>
              <div className="text-sm text-muted-foreground">Gather expertise and insights</div>
            </Label>
          </div>
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="destruction" id="destruction" className="mt-1" />
            <Label htmlFor="destruction" className="cursor-pointer flex-1">
              <div className="font-medium">Call for Destruction</div>
              <div className="text-sm text-muted-foreground">Identify issues or inefficiencies</div>
            </Label>
          </div>
        </RadioGroup>
      </div>
    </FormFieldMessage>
  );
};

interface FunctionalAreaMessageProps {
  onSelect: (areaId: string, areaName: string) => void;
  callType: CallType;
  channelId?: string | null;
  isPersonal?: boolean;
  onCreateNew?: (name: string) => void;
  disabled?: boolean;
  contextLabel?: 'call' | 'kb'; // 'call' for Call creation, 'kb' for KB creation
}

export const FunctionalAreaMessage = ({ 
  onSelect, 
  callType, 
  channelId, 
  isPersonal, 
  onCreateNew,
  disabled,
  contextLabel = 'call'
}: FunctionalAreaMessageProps) => {
  const [showNewInput, setShowNewInput] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const callTypeLabel = callType === 'solution' ? 'Solution' : callType === 'knowledge' ? 'Knowledge' : 'Destruction';
  
  // Fetch domains based on channel
  const { data: allDomains, isLoading: domainsLoading } = useDomains();
  
  // Filter domains by channel if a channel is selected, otherwise show all for personal
  const filteredDomains = allDomains?.filter(domain => {
    if (isPersonal) {
      // Personal mode: show all domains
      return true;
    }
    if (channelId) {
      // Channel mode: show ONLY domains for this specific channel (strict filtering)
      return domain.channel_id === channelId;
    }
    return false; // No channel selected and not personal - show nothing
  }) || [];

  // Sort domains alphabetically and map to display format
  const displayAreas = filteredDomains
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(d => ({ id: d.id, name: d.name }));

  const handleCreateNew = () => {
    if (newAreaName.trim() && onCreateNew) {
      onCreateNew(newAreaName.trim());
      setShowNewInput(false);
      setNewAreaName('');
    }
  };

  const handleAreaSelect = (areaId: string) => {
    if (areaId === '__create_new__') {
      setShowNewInput(true);
    } else {
      const selectedArea = displayAreas.find(a => a.id === areaId);
      onSelect(areaId, selectedArea?.name || areaId);
    }
  };

  // Context-aware question text
  const questionText = contextLabel === 'kb'
    ? 'Which functional area does this Knowledge Base belong to?'
    : `Great choice! Now select the functional area for your Call for ${callTypeLabel}:`;
  
  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">{questionText}</p>
        
        {domainsLoading ? (
          <div className="text-sm text-muted-foreground">Loading functional areas...</div>
        ) : showNewInput ? (
          <div className="space-y-3">
            <Input
              placeholder="Enter new functional area name..."
              value={newAreaName}
              onChange={(e) => setNewAreaName(e.target.value)}
              disabled={disabled}
              autoFocus
            />
            <div className="flex gap-2">
              <Button 
                onClick={handleCreateNew} 
                disabled={!newAreaName.trim() || disabled}
                className="flex-1"
              >
                Create
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowNewInput(false)}
                disabled={disabled}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between"
                disabled={disabled}
              >
                <span className="text-muted-foreground">Choose a functional area...</span>
                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput 
                  placeholder="Search functional areas..." 
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  <CommandEmpty>No functional area found.</CommandEmpty>
                  <CommandGroup>
                    {displayAreas
                      .filter(area => 
                        area.name.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map(area => (
                        <CommandItem
                          key={area.id}
                          value={area.name}
                          onSelect={() => {
                            handleAreaSelect(area.id);
                            setOpen(false);
                            setSearchQuery('');
                          }}
                        >
                          {area.name}
                        </CommandItem>
                      ))}
                    {isPersonal && (
                      <CommandItem
                        value="create-new-area"
                        onSelect={() => {
                          handleAreaSelect('__create_new__');
                          setOpen(false);
                          setSearchQuery('');
                        }}
                        className="text-primary"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Create New Functional Area
                      </CommandItem>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </FormFieldMessage>
  );
};

interface BountyMessageProps {
  onSelect: (isBounty: boolean) => void;
  disabled?: boolean;
}

export const BountyMessage = ({ onSelect, disabled }: BountyMessageProps) => {
  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Would you like to add a bounty to incentivize contributions?
        </p>
        <RadioGroup onValueChange={(value) => onSelect(value === 'yes')} disabled={disabled}>
          <div className="flex items-center space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="yes" id="bounty-yes" />
            <Label htmlFor="bounty-yes" className="cursor-pointer">Yes, add a bounty</Label>
          </div>
          <div className="flex items-center space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="no" id="bounty-no" />
            <Label htmlFor="bounty-no" className="cursor-pointer">No bounty</Label>
          </div>
        </RadioGroup>
      </div>
    </FormFieldMessage>
  );
};

interface BountyDetailsMessageProps {
  onSubmit: (amount: string, deadline: string) => void;
  disabled?: boolean;
}

export const BountyDetailsMessage = ({ onSubmit, disabled }: BountyDetailsMessageProps) => {
  const [amount, setAmount] = useState('');
  const [deadline, setDeadline] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Please provide the bounty details:</p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="bounty-amount" className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4" />
              Bounty Amount
            </Label>
            <Input
              id="bounty-amount"
              type="number"
              placeholder="Enter amount..."
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={disabled}
            />
          </div>
          <div>
            <Label htmlFor="bounty-deadline" className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4" />
              Deadline
            </Label>
            <Input
              id="bounty-deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              disabled={disabled}
            />
          </div>
          <Button
            onClick={() => onSubmit(amount, deadline)}
            disabled={!amount || !deadline || disabled}
            className="w-full"
          >
            Continue
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};

interface CallDetailsMessageProps {
  onSubmit: (title: string, description: string, requirements: string) => void;
  disabled?: boolean;
}

export const CallDetailsMessage = ({ onSubmit, disabled }: CallDetailsMessageProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requirements, setRequirements] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Now let's define the details of your Call:
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="call-title">Title</Label>
            <Input
              id="call-title"
              placeholder="Enter a clear, concise title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={disabled}
            />
          </div>
          <div>
            <Label htmlFor="call-description">Description</Label>
            <Textarea
              id="call-description"
              placeholder="Describe the challenge or objective..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={disabled}
              rows={4}
            />
          </div>
          <div>
            <Label htmlFor="call-requirements">Requirements</Label>
            <Textarea
              id="call-requirements"
              placeholder="What are the key requirements or criteria?"
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              disabled={disabled}
              rows={3}
            />
          </div>
          <Button
            onClick={() => onSubmit(title, description, requirements)}
            disabled={!title || !description || disabled}
            className="w-full"
          >
            Continue
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};

interface RolesMessageProps {
  onSubmit: (curator: string, confirmer: string, contributors: string[]) => void;
  disabled?: boolean;
}

interface PlatformUser {
  id: string;
  display_name: string;
  username: string | null;
  avatar_url: string | null;
  email: string;
}

// Single-select member picker component
const SingleMemberSelect = ({ 
  label, 
  placeholder,
  selectedUserId,
  onUserSelect,
  onUserClear,
  manualEmail,
  onManualEmailChange,
  platformUsers,
  disabled,
  isLoading
}: {
  label: string;
  placeholder: string;
  selectedUserId: string | null;
  onUserSelect: (userId: string) => void;
  onUserClear: () => void;
  manualEmail: string;
  onManualEmailChange: (email: string) => void;
  platformUsers: PlatformUser[];
  disabled?: boolean;
  isLoading?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [useManualEmail, setUseManualEmail] = useState(false);
  const selectedUser = platformUsers.find(u => u.id === selectedUserId);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {!useManualEmail ? (
        <>
          {selectedUser ? (
            <div className="flex items-center gap-2 p-2 rounded-md border border-border bg-accent/20">
              <Avatar className="h-6 w-6">
                <AvatarImage src={selectedUser.avatar_url || ''} />
                <AvatarFallback className="text-xs">
                  {selectedUser.display_name?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 text-sm">{selectedUser.display_name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={onUserClear}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between font-normal"
                  disabled={disabled || isLoading}
                >
                  <span className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    {placeholder}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search members..." />
                  <CommandList>
                    <CommandEmpty>No members found.</CommandEmpty>
                    <CommandGroup>
                      {platformUsers.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={user.display_name}
                          onSelect={() => {
                            onUserSelect(user.id);
                            setOpen(false);
                          }}
                        >
                          <Avatar className="h-6 w-6 mr-2">
                            <AvatarImage src={user.avatar_url || ''} />
                            <AvatarFallback className="text-xs">
                              {user.display_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        <div className="flex flex-col">
                            <span className="text-sm">{user.display_name}</span>
                            <span className="text-xs text-muted-foreground">
                              @{user.username || user.display_name?.toLowerCase().replace(/\s+/g, '-')}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground"
            onClick={() => setUseManualEmail(true)}
            disabled={disabled}
          >
            Or enter email manually
          </Button>
        </>
      ) : (
        <>
          <Input
            type="email"
            placeholder="email@example.com"
            value={manualEmail}
            onChange={(e) => onManualEmailChange(e.target.value)}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground"
            onClick={() => setUseManualEmail(false)}
            disabled={disabled}
          >
            Or select from platform members
          </Button>
        </>
      )}
    </div>
  );
};

// Multi-select member picker component
const MultiMemberSelect = ({ 
  label, 
  placeholder,
  selectedUserIds,
  onUserToggle,
  manualEmails,
  onManualEmailsChange,
  platformUsers,
  disabled,
  isLoading
}: {
  label: string;
  placeholder: string;
  selectedUserIds: string[];
  onUserToggle: (userId: string) => void;
  manualEmails: string;
  onManualEmailsChange: (emails: string) => void;
  platformUsers: PlatformUser[];
  disabled?: boolean;
  isLoading?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const selectedUsers = platformUsers.filter(u => selectedUserIds.includes(u.id));

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {/* Selected users display */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1 p-2 rounded-md border border-border bg-accent/20">
          {selectedUsers.map((user) => (
            <div key={user.id} className="flex items-center gap-1 bg-background rounded-full px-2 py-1">
              <Avatar className="h-4 w-4">
                <AvatarImage src={user.avatar_url || ''} />
                <AvatarFallback className="text-[10px]">
                  {user.display_name?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs">{user.display_name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0"
                onClick={() => onUserToggle(user.id)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled || isLoading}
          >
            <span className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              {placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search members..." />
            <CommandList>
              <CommandEmpty>No members found.</CommandEmpty>
              <CommandGroup>
                {platformUsers.map((user) => {
                  const isSelected = selectedUserIds.includes(user.id);
                  return (
                    <CommandItem
                      key={user.id}
                      value={user.display_name}
                      onSelect={() => onUserToggle(user.id)}
                    >
                      <div className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50"
                      )}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <Avatar className="h-6 w-6 mr-2">
                        <AvatarImage src={user.avatar_url || ''} />
                        <AvatarFallback className="text-xs">
                          {user.display_name?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm">{user.display_name}</span>
                        <span className="text-xs text-muted-foreground">
                          @{user.username || user.display_name?.toLowerCase().replace(/\s+/g, '-')}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Or add by email (comma-separated)</Label>
        <Input
          type="text"
          placeholder="contributor1@example.com, contributor2@example.com"
          value={manualEmails}
          onChange={(e) => onManualEmailsChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const RolesMessage = ({ onSubmit, disabled }: RolesMessageProps) => {
  const { user } = useAuth();
  
  // State for curator
  const [curatorUserId, setCuratorUserId] = useState<string | null>(null);
  const [curatorEmail, setCuratorEmail] = useState('');
  
  // State for confirmer
  const [confirmerUserId, setConfirmerUserId] = useState<string | null>(null);
  const [confirmerEmail, setConfirmerEmail] = useState('');
  
  // State for contributors
  const [contributorUserIds, setContributorUserIds] = useState<string[]>([]);
  const [contributorEmails, setContributorEmails] = useState('');

  // Fetch platform users
  const { data: platformUsers = [], isLoading } = useQuery({
    queryKey: ['platform-users-for-roles', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, display_name, username, avatar_url, email')
        .order('display_name');
      return (data?.filter(p => p.id !== user?.id) || []) as PlatformUser[];
    },
    enabled: !!user?.id
  });

  const handleContributorToggle = (userId: string) => {
    setContributorUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSubmit = () => {
    // Get curator display value - use @username for platform users, email for manual entry
    const curatorUser = curatorUserId ? platformUsers.find(u => u.id === curatorUserId) : null;
    const curatorValue = curatorUser 
      ? `@${curatorUser.username || curatorUser.display_name?.toLowerCase().replace(/\s+/g, '-')}`
      : curatorEmail;
    
    // Get confirmer display value - use @username for platform users, email for manual entry
    const confirmerUser = confirmerUserId ? platformUsers.find(u => u.id === confirmerUserId) : null;
    const confirmerValue = confirmerUser
      ? `@${confirmerUser.username || confirmerUser.display_name?.toLowerCase().replace(/\s+/g, '-')}`
      : confirmerEmail;
    
    // Get contributors - use @username for platform users, email for manual entries
    const contributorValues = [
      ...contributorUserIds.map(id => {
        const user = platformUsers.find(u => u.id === id);
        return user ? `@${user.username || user.display_name?.toLowerCase().replace(/\s+/g, '-')}` : null;
      }).filter(Boolean),
      ...contributorEmails.split(',').map(e => e.trim()).filter(Boolean)
    ] as string[];
    
    onSubmit(curatorValue, confirmerValue, contributorValues);
  };

  const hasCurator = curatorUserId || curatorEmail;

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Finally, let's assign roles for this Call:
        </p>
        <div className="space-y-4">
          <SingleMemberSelect
            label="Creator (Leader)"
            placeholder="Search platform members..."
            selectedUserId={curatorUserId}
            onUserSelect={setCuratorUserId}
            onUserClear={() => setCuratorUserId(null)}
            manualEmail={curatorEmail}
            onManualEmailChange={setCuratorEmail}
            platformUsers={platformUsers}
            disabled={disabled}
            isLoading={isLoading}
          />
          
          <SingleMemberSelect
            label="Confirmer"
            placeholder="Search platform members..."
            selectedUserId={confirmerUserId}
            onUserSelect={setConfirmerUserId}
            onUserClear={() => setConfirmerUserId(null)}
            manualEmail={confirmerEmail}
            onManualEmailChange={setConfirmerEmail}
            platformUsers={platformUsers}
            disabled={disabled}
            isLoading={isLoading}
          />
          
          <MultiMemberSelect
            label="Contributors"
            placeholder="Add contributors..."
            selectedUserIds={contributorUserIds}
            onUserToggle={handleContributorToggle}
            manualEmails={contributorEmails}
            onManualEmailsChange={setContributorEmails}
            platformUsers={platformUsers}
            disabled={disabled}
            isLoading={isLoading}
          />
          
          <Button
            onClick={handleSubmit}
            disabled={!hasCurator || disabled}
            className="w-full"
          >
            Review Call
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};

interface ReviewMessageProps {
  formData: CallFormData;
  onConfirm: () => void;
  onEdit: () => void;
  disabled?: boolean;
}

// Get Call Type emoji and abbreviation
const getCallTypeInfo = (type: CallType | null) => {
  switch (type) {
    case 'solution': return { emoji: '🎯', abbr: 'CfS', label: 'Solution' };
    case 'knowledge': return { emoji: '📚', abbr: 'CfK', label: 'Knowledge' };
    case 'destruction': return { emoji: '🔥', abbr: 'CfD', label: 'Destruction' };
    default: return { emoji: '📋', abbr: 'Call', label: 'Call' };
  }
};

export const ReviewMessage = ({ formData, onConfirm, onEdit, disabled }: ReviewMessageProps) => {
  const callTypeInfo = getCallTypeInfo(formData.type);
  const functionalAreaLabel = formData.functionalAreaName || formData.functionalArea;

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Please review your Call before submitting:
        </p>
        
        {/* Comprehensive Call Document Preview */}
        <Card className="p-4 space-y-4 bg-accent/10 border-2">
          {/* Header */}
          <div className="border-b pb-3">
            <div className="flex items-center gap-2 text-lg font-bold">
              <span>{callTypeInfo.emoji}</span>
              <span>CALL FOR {callTypeInfo.label.toUpperCase()} ({callTypeInfo.abbr})</span>
            </div>
            <div className="text-xl font-semibold mt-2">{formData.title || 'Untitled Call'}</div>
            {formData.channelName && (
              <div className="text-sm text-muted-foreground mt-1">
                Channel: {formData.channelName} • Status: Open
              </div>
            )}
          </div>

          {/* Bounty & Deadline */}
          {(formData.isBounty || formData.deadline) && (
            <div className="flex gap-4 text-sm">
              {formData.isBounty && (
                <div className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  <span className="font-medium">${formData.bountyAmount} USD</span>
                </div>
              )}
              {formData.deadline && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <span>Deadline: {formData.deadline}</span>
                </div>
              )}
            </div>
          )}

          {/* Knowledge Domains */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">📌 KNOWLEDGE DOMAIN</div>
            <div className="text-sm">{functionalAreaLabel}</div>
          </div>

          {/* Problem Statement / Description */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">🧩 PROBLEM STATEMENT</div>
            <div className="text-sm whitespace-pre-wrap">{formData.description || 'No description provided'}</div>
          </div>

          {/* Objectives (if provided) */}
          {formData.objectives && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">📌 TARGET OBJECTIVES</div>
              <div className="text-sm whitespace-pre-wrap">{formData.objectives}</div>
            </div>
          )}

          {/* Requirements */}
          {formData.requirements && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">
                {formData.type === 'knowledge' ? '📚 REQUIRED KNOWLEDGE' : '✅ DESIRED OUTPUTS'}
              </div>
              <div className="text-sm whitespace-pre-wrap">{formData.requirements}</div>
            </div>
          )}

          {/* Evaluation Criteria (if provided) */}
          {formData.evaluationCriteria && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">📊 EVALUATION CRITERIA</div>
              <div className="text-sm whitespace-pre-wrap">{formData.evaluationCriteria}</div>
            </div>
          )}

          {/* CfK-specific: Connection Info */}
          {formData.type === 'knowledge' && formData.connectedToCfS && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">🔗 CONNECTED TO CfS</div>
              <div className="text-sm">
                This Call for Knowledge is connected to a Call for Solution
                {formData.cfSHasBounty && ' that has a Solution Bounty'}
              </div>
            </div>
          )}

          {/* Attribution Model (for bounty) */}
          {formData.isBounty && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">🧬 ATTRIBUTION & COMPENSATION</div>
              <div className="text-sm space-y-1">
                <div>• Curator: {formData.curatorPercentage}%</div>
                <div>• Contributors: {formData.contributorPercentage}%</div>
                <div>• Confirmer: {formData.confirmerPercentage}%</div>
                {formData.composerPercentage && parseFloat(formData.composerPercentage) > 0 && (
                  <div>• Composer: {formData.composerPercentage}%</div>
                )}
              </div>
            </div>
          )}

          {/* Role Assignments */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">🧑‍💼 ROLE ASSIGNMENTS</div>
            <div className="text-sm space-y-1">
              <div><span className="font-medium">Creator:</span> {formData.curator || 'Open'}</div>
              <div><span className="font-medium">Confirmer:</span> {formData.confirmer || 'Open'}</div>
              {formData.contributors && formData.contributors.length > 0 && (
                <div><span className="font-medium">Contributors:</span> {formData.contributors.join(', ')}</div>
              )}
              {formData.composer && (
                <div><span className="font-medium">Composer:</span> {formData.composer}</div>
              )}
            </div>
          </div>

          {/* Next Steps */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">🚀 NEXT STEPS</div>
            <div className="text-sm">
              Upon submission, this Call will be published and visible to channel members who can respond with their contributions.
            </div>
          </div>
        </Card>
        
        <div className="flex gap-2">
          <Button onClick={onEdit} variant="outline" className="flex-1" disabled={disabled}>
            Edit
          </Button>
          <Button onClick={onConfirm} className="flex-1" disabled={disabled}>
            Submit Call
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};
